import React, { useState } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../../firebase/firebase';
import { validatePasswordComplete } from '../../utils/passwordValidator';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { useTranslation } from '../../hooks/useTranslation';
import { createOrUpdateUserProfile } from '../../services/userService';
import './Auth.css';

const SignUp = ({ onSwitchToLogin, embedded = false }) => {
  const t = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate password in real-time
    if (name === 'password') {
      const validation = validatePasswordComplete(value);
      setPasswordValidation(validation);
    }
  };

  const validateForm = () => {
    // Validate name fields
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError(t('landingPage.auth.signup.errors.nameRequired'));
      return false;
    }

    // Validate password strength
    const passwordCheck = validatePasswordComplete(formData.password);
    if (!passwordCheck.isValid) {
      setError(passwordCheck.errors[0]); // Show first error
      return false;
    }
    
    // Check password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError(t('landingPage.auth.signup.errors.passwordMismatch'));
      return false;
    }

    // Warn about weak passwords (but allow if they meet requirements)
    if (passwordCheck.warnings.length > 0 && passwordCheck.strength === 'weak') {
      setError(`Warning: ${passwordCheck.warnings[0]}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      // Create user profile in Firestore with firstName and lastName
      try {
        await createOrUpdateUserProfile(userCredential.user.uid, {
          email: formData.email,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim()
        });
      } catch (profileError) {
        console.error('Error creating user profile:', profileError);
        // Don't block signup if profile creation fails
      }
      
      // Send email verification
      await sendEmailVerification(userCredential.user);
      
      setSuccess(t('landingPage.auth.signup.success'));
      
      // Clear form
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: ''
      });
      
    } catch (error) {
      console.error('Sign up error:', error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError(t('landingPage.auth.signup.errors.emailInUse'));
          break;
        case 'auth/invalid-email':
          setError(t('landingPage.auth.signup.errors.invalidEmail'));
          break;
        case 'auth/weak-password':
          setError(t('landingPage.auth.signup.errors.weakPassword'));
          break;
        case 'auth/operation-not-allowed':
          setError(t('landingPage.auth.signup.errors.operationNotAllowed'));
          break;
        default:
          setError(t('landingPage.auth.signup.errors.default'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    setError('');
    setSuccess('');

    try {
      await signInWithPopup(auth, googleProvider);
      // Success - user will be redirected by AuthContext
    } catch (error) {
      console.error('Google sign-up error:', error);
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          setError(t('landingPage.auth.signup.errors.popupClosed'));
          break;
        case 'auth/popup-blocked':
          setError(t('landingPage.auth.signup.errors.popupBlocked'));
          break;
        case 'auth/cancelled-popup-request':
          setError(t('landingPage.auth.signup.errors.cancelled'));
          break;
        case 'auth/account-exists-with-different-credential':
          setError(t('landingPage.auth.signup.errors.accountExists'));
          break;
        default:
          setError(t('landingPage.auth.signup.errors.googleFailed'));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const content = (
    <>
      {!embedded && <h2>{t('landingPage.auth.signup.title')}</h2>}
      {!embedded && <p className="auth-subtitle">{t('landingPage.auth.signup.subtitle')}</p>}
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <button 
        type="button"
        onClick={handleGoogleSignUp}
        className="auth-button google"
        disabled={googleLoading || loading}
      >
        {googleLoading ? t('landingPage.auth.signup.signingUp') : t('landingPage.auth.signup.continueWithGoogle')}
      </button>
      
      {!showEmailForm ? (
        <>
          <div className="auth-divider">
            <span>{t('landingPage.auth.signup.or')}</span>
          </div>
          
          <button 
            type="button"
            onClick={() => setShowEmailForm(true)}
            className="link-button"
            style={{ 
              width: '100%', 
              padding: '0.75rem',
              marginTop: '0.5rem',
              textAlign: 'center',
              fontSize: '0.95rem'
            }}
            disabled={googleLoading || loading}
          >
            {t('landingPage.auth.signup.continueWithEmail')}
          </button>
        </>
      ) : (
        <>
          <div className="auth-divider">
            <span>{t('landingPage.auth.signup.or')}</span>
          </div>
          
          <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('landingPage.auth.signup.firstName')}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder={t('landingPage.auth.signup.firstNamePlaceholder')}
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">{t('landingPage.auth.signup.lastName')}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder={t('landingPage.auth.signup.lastNamePlaceholder')}
                required
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('landingPage.auth.signup.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('landingPage.auth.signup.emailPlaceholder')}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('landingPage.auth.signup.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('landingPage.auth.signup.passwordPlaceholder')}
              required
              disabled={loading}
            />
            {passwordValidation && (
              <PasswordStrengthIndicator strength={passwordValidation.strength} />
            )}
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
              {t('landingPage.auth.signup.passwordHint')}
            </small>
            {passwordValidation && passwordValidation.warnings.length > 0 && (
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#f39c12' }}>
                ⚠️ {passwordValidation.warnings[0]}
              </small>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('landingPage.auth.signup.confirmPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('landingPage.auth.signup.confirmPasswordPlaceholder')}
              required
              disabled={loading}
            />
          </div>
          
          <button 
            type="submit" 
            className="auth-button primary"
            disabled={loading || googleLoading}
          >
            {loading ? t('landingPage.auth.signup.creatingAccount') : t('landingPage.auth.signup.createAccount')}
          </button>
        </form>
        </>
      )}
        
        <div className="auth-switch">
          <p>
            {t('landingPage.auth.signup.alreadyHaveAccount')}{' '}
            <button 
              type="button" 
              className="link-button"
              onClick={onSwitchToLogin}
              disabled={loading || googleLoading}
            >
              {t('landingPage.auth.signup.signIn')}
            </button>
          </p>
        </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        {content}
      </div>
    </div>
  );
};

export default SignUp; 